import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useMemo } from 'react';
import { useSwimlaneInternalEventHandler } from '../api';
import { CardVirtualizationContext, useSwimlaneRootProps, useSwimlaneState } from '../context';
import type { IHeader } from '../types';

import { SwimlaneGrid } from './body/SwimlaneGrid';
import {
  CARD_WIDTH,
  COMPACT_CARD_WIDTH,
  GRID_GAP,
  GROUP_ROW_HEADER_HEIGHT,
  ROW_HEIGHT_MIN,
} from './constants';
import { CardsDNDContextProvider } from './dnd';
import { AllItemsFiltered } from './empty-state/AllItemsFiltered';
import { NoItems } from './empty-state/NoItems';
import { NoSubItems } from './empty-state/NoSubItems';
import { ColumnHeaders } from './headers/columns/ColumnHeaders';
import { RowHeaders } from './headers/rows/RowHeaders';

interface ILayoutRoot {
  scrollContainer: HTMLDivElement;
}

interface IFlag {
  [key: string]: boolean;
}

function generateCompositeKey(headers: IHeader[], index: number, flags: IFlag): string {
  const flagsString = Object.keys(flags).join('-');
  return `${headers[index]?.id}${flagsString}` || index.toString();
}

export function VirtualizerRoot({ scrollContainer }: ILayoutRoot) {
  const {
    rowHeaders,
    columnHeaders,
    isCompact,
    isItemDateVisible,
    isItemDescriptionVisible,
    isItemDetailsVisible,
    emptyState,
  } = useSwimlaneRootProps();
  const { getRowHeight } = useSwimlaneState();

  const getRowItemKey = useCallback(
    (index: number) =>
      generateCompositeKey(rowHeaders, index, {
        isCompact,
        isItemDateVisible,
        isItemDescriptionVisible,
        isItemDetailsVisible,
      }),
    [rowHeaders, isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible],
  );

  const getColumnItemKey = useCallback(
    (index: number) =>
      generateCompositeKey(columnHeaders, index, {
        isCompact,
        isItemDateVisible,
        isItemDescriptionVisible,
        isItemDetailsVisible,
      }),
    [columnHeaders, isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible],
  );

  // Count of row-virtualizer items that sit in the virtualizer's coordinate space but are
  // absent from the grid body's DOM height (SwimlaneGrid subtracts each group header + its gap
  // from gridHeight so group headers render only in the row-headers gutter).
  // Because the virtualizer's `totalSize` stays larger than the DOM's scrollable content by
  // exactly this many pixels, the default visibility check drops rows whose `start` sits in the
  // trailing `offsetPx` band — those rows are visible in the DOM but never rendered.
  // We compensate by extending the range's bottom edge by enough items to cover that band.
  const rowRangePaddingItems = useMemo(() => {
    let offsetPx = 0;
    for (const header of rowHeaders) {
      if ('isGroupHeader' in header && header.isGroupHeader === true) {
        offsetPx += GROUP_ROW_HEADER_HEIGHT.value + GRID_GAP.value;
      }
    }
    if (offsetPx === 0) return 0;
    return Math.ceil(offsetPx / (ROW_HEIGHT_MIN.value + GRID_GAP.value)) + 1;
  }, [rowHeaders]);

  const rowRangeExtractor = useCallback(
    ({
      startIndex,
      endIndex,
      overscan,
      count,
    }: {
      startIndex: number;
      endIndex: number;
      overscan: number;
      count: number;
    }) => {
      const start = Math.max(0, startIndex - overscan);
      const end = Math.min(count - 1, endIndex + overscan + rowRangePaddingItems);
      const result: number[] = [];
      for (let i = start; i <= end; i++) result.push(i);
      return result;
    },
    [rowRangePaddingItems],
  );

  const columnVirtualizer = useVirtualizer({
    count: columnHeaders.length,
    estimateSize: () => (isCompact ? COMPACT_CARD_WIDTH.value : CARD_WIDTH.value),
    gap: GRID_GAP.value,
    getScrollElement: () => scrollContainer,
    horizontal: true,
    overscan: 1,
    getItemKey: getColumnItemKey,
  });

  const rowVirtualizer = useVirtualizer({
    count: Math.max(rowHeaders.length, 1),
    estimateSize: (index: number) => {
      const header = rowHeaders[index];
      if (header && 'isGroupHeader' in header && header.isGroupHeader === true) {
        return GROUP_ROW_HEADER_HEIGHT.value;
      }
      const rowID = header?.id ?? 'null';
      return getRowHeight(rowID);
    },
    measureElement: (element, _entry, instance) => {
      const index = instance.indexFromElement(element);
      const header = rowHeaders[index];
      if (header && 'isGroupHeader' in header && header.isGroupHeader === true) {
        return GROUP_ROW_HEADER_HEIGHT.value;
      }
      return getRowHeight(header?.id ?? 'null');
    },
    gap: GRID_GAP.value,
    getScrollElement: () => scrollContainer,
    getItemKey: getRowItemKey,
    overscan: 1,
    rangeExtractor: rowRangeExtractor,
  });

  const onRowHeightChange = useCallback(() => {
    rowHeaders.forEach((header, index) => {
      if ('isGroupHeader' in header && header.isGroupHeader === true) {
        rowVirtualizer.resizeItem(index, GROUP_ROW_HEADER_HEIGHT.value);
      } else {
        rowVirtualizer.resizeItem(index, getRowHeight(header.id));
      }
    });
  }, [getRowHeight, rowHeaders, rowVirtualizer]);
  useSwimlaneInternalEventHandler('swimlaneRowHeightChange', onRowHeightChange);

  return (
    <>
      <ColumnHeaders columnVirtualizer={columnVirtualizer} />
      <RowHeaders rowVirtualizer={rowVirtualizer} />
      <CardsDNDContextProvider onDragEnd={rowVirtualizer.measure}>
        {emptyState === 'no-items' && <NoItems />}
        {emptyState === 'filtered' && <AllItemsFiltered />}
        {emptyState === 'no-sub-items' && <NoSubItems />}
        {emptyState === 'no-sub-items-portfolio' && <NoSubItems isMaster />}
        {!emptyState && (
          <CardVirtualizationContext.Provider value={rowVirtualizer}>
            <SwimlaneGrid columnVirtualizer={columnVirtualizer} rowVirtualizer={rowVirtualizer} />
          </CardVirtualizationContext.Provider>
        )}
      </CardsDNDContextProvider>
    </>
  );
}
